// Mirror of the server's work-type normalisation, needed here because the Add
// Work lane lets an agent TYPE a work type freehand (`<Input list="add-work-types">`)
// while WorkDetail passes the canonical enum. Comparing the raw string against
// "OTHER" therefore misses `Other`, `other`, `Photo `, or a typo — all of which
// `normalize_work_type` collapses to OTHER server-side, after which
// `media_type_for_work_type` returns None and `_maybe_enqueue_ledger_append`
// returns before it ever reads the provenance row.
//
// SOURCE OF TRUTH: `database/orm.py` (`WORK_TYPES`, `_WORK_TYPE_LEGACY_MAP`,
// `normalize_work_type`) and `provenance_input.py` (`WORK_TYPE_TO_MEDIA_TYPE`,
// where OTHER maps to None). Kept as a mirror rather than derived from
// `GET /works/types`, because that endpoint returns `SELECT DISTINCT work.type`
// — the types that happen to EXIST in the data, not the types that are valid.
// A legitimate enum member with zero works would be flagged "unrecognised" and
// the warning would be a lie.
//
// Drift here can only produce a wrong WARNING, never a wrong write: the server
// re-normalises everything it stores.

export const WORK_TYPES = [
  "AUDIO",
  "DIGITAL_ART",
  "OTHER",
  "PHOTOGRAPH",
  "VIDEO",
  "WRITTEN_WORD",
] as const;

const LEGACY_MAP: Record<string, string> = {
  book: "WRITTEN_WORD",
  document: "WRITTEN_WORD",
  "blog post": "WRITTEN_WORD",
  novel: "WRITTEN_WORD",
  fiction: "WRITTEN_WORD",
  email: "WRITTEN_WORD",
  "substack post": "WRITTEN_WORD",
  audio: "AUDIO",
  music: "AUDIO",
  photo: "PHOTOGRAPH",
  photograph: "PHOTOGRAPH",
  "pre-genai photograph": "PHOTOGRAPH",
  video: "VIDEO",
  "digital art": "DIGITAL_ART",
  digital_art: "DIGITAL_ART",
};

/** Exactly `database.orm.normalize_work_type`: canonical passes through,
 *  legacy free text maps, everything else (including blank) becomes OTHER. */
export function normalizeWorkType(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "OTHER";
  const s = String(raw).trim();
  if (!s) return "OTHER";
  const canonical = s.toUpperCase().replace(/ /g, "_");
  if ((WORK_TYPES as readonly string[]).includes(canonical)) return canonical;
  return LEGACY_MAP[s.toLowerCase()] ?? "OTHER";
}

/** False when the work type maps to no coc MediaType — i.e. when attaching a
 *  source file cannot possibly produce a ledger entry. OTHER is the only such
 *  canonical member, and every unrecognised string normalises to it. */
export function producesLedgerEntry(raw: string | null | undefined): boolean {
  return normalizeWorkType(raw) !== "OTHER";
}

/** True when the typed text is not a work type the server recognises at all
 *  (as opposed to a deliberate, correctly spelled "Other"). Drives the more
 *  specific warning copy — an agent who typed a typo needs to know it was a
 *  typo, not that they picked a bad category. */
export function isUnrecognisedWorkType(raw: string | null | undefined): boolean {
  if (producesLedgerEntry(raw)) return false;
  const s = String(raw ?? "").trim();
  if (!s) return false;
  return s.toUpperCase().replace(/ /g, "_") !== "OTHER";
}
